import { getRouteApi } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'

import AddIcon from '@/assets/icons/add.svg?react'
import EditIcon from '@/assets/icons/edit.svg?react'
import Button from '@/components/Button/Button'
import LinkText from '@/components/LinkText/LinkText'
import Table, { type TableAction } from '@/components/Table/Table'
import { TABLE_CELL_WIDTH } from '@/components/Table/table-cell-width'
import Tag from '@/components/Tag/Tag'
import ToggleGroup, {
  type ToggleGroupOption
} from '@/components/ToggleGroup/ToggleGroup'
import ExportButton from '@/components/common/ExportButton'
import Filters, { type Filter } from '@/components/common/Filters/Filters'
import DEFAULT_PAGE_SIZE from '@/constants/default-page-size'
import { useColumns } from '@/hooks/useColumns'
import { useDebounceSearch } from '@/hooks/useDebounceSearch'
import { usePagination } from '@/hooks/usePagination'
import { useScreenResolution } from '@/hooks/useScreenResolution'
import useAuthStore from '@/store/useAuthStore'
import { checkIfFilterSelected } from '@/utils/check-if-filters-selected'
import { formatDate } from '@/utils/format-date'
import { getFilterValue } from '@/utils/get-filter-value'

import styles from './SchoolBreaksView.module.scss'
import { schoolBreakStatusToTagMap } from '../constants/school-break-status-to-tag-map'
import { type SchoolBreaksFilterKey } from '../constants/school-settings-filters'
import useSchoolBreaksDownload from '../mutations/useSchoolBreaksDownload'
import { useSchoolBreaks } from '../queries/useSchoolBreaks'
import useSchoolBreaksCount from '../queries/useSchoolBreaksCount'
import useSchoolBreaksYearsOptions from '../queries/useSchoolBreaksYearsOptions'
import type {
  SchoolBreak,
  SchoolYearAndBreakToggle,
  SchoolBreakStatus
} from '../types'

const routeApi = getRouteApi('/_auth/school-settings/breaks/')

const SchoolBreaksView = () => {
  const { t } = useTranslation('schoolSettings')

  const { user } = useAuthStore()

  const { isMobile } = useScreenResolution()

  const search = routeApi.useSearch()
  const navigate = routeApi.useNavigate()

  const pagination = usePagination(routeApi)

  const { data: schoolBreaks, isLoading: isSchoolBreaksLoading } =
    useSchoolBreaks(search)

  const { data: schoolBreaksCount, isLoading: isSchoolBreaksCountLoading } =
    useSchoolBreaksCount({ status: search.status })

  const { data: yearsOptions, isLoading: isYearsOptionsLoading } =
    useSchoolBreaksYearsOptions()

  const { columns, ...manageColumns } = useColumns<SchoolBreak>(
    [
      {
        key: 'name',
        label: t('header.name'),
        width: isMobile ? TABLE_CELL_WIDTH.MD : undefined,
        render: (value: SchoolBreak) => (
          <LinkText
            to="/school-settings/breaks/$breakId"
            params={{
              breakId: value.id
            }}
          >
            {value.name}
          </LinkText>
        )
      },
      {
        label: t('header.start-date'),
        key: 'start_date',
        render: (value: SchoolBreak) => formatDate(value.startDate),
        width: isMobile ? TABLE_CELL_WIDTH.LG : undefined
      },
      {
        label: t('header.end-date'),
        key: 'end_date',
        render: (value: SchoolBreak) => formatDate(value.endDate),
        width: isMobile ? TABLE_CELL_WIDTH.LG : undefined
      },
      {
        dataIndex: 'status',
        key: 'status',
        label: t('header.status'),
        width: 140,
        render: (value: SchoolBreakStatus) => (
          <Tag {...schoolBreakStatusToTagMap(t)[value]} />
        )
      }
    ],
    { routeApi }
  )

  const handleToggle = (value: SchoolYearAndBreakToggle) => {
    navigate({
      search: prev => ({
        ...prev,
        status: value,
        page: 1
      })
    })
  }

  const { searchValue, handleChangeSearch } = useDebounceSearch(routeApi)

  const toggleOptions: ToggleGroupOption<SchoolYearAndBreakToggle>[] = [
    {
      label: t('button.active-and-future'),
      value: 'active-and-future'
    },
    {
      label: t('button.past'),
      value: 'past'
    }
  ]

  const handleChangeFilter = (
    key: SchoolBreaksFilterKey,
    value?: string | string[] | Date
  ) => {
    navigate({
      search: previousValue => ({
        ...previousValue,
        page: 1,
        [key]: getFilterValue(value)
      })
    })
  }

  const filters: Filter[] = [
    {
      variant: 'multiselect',
      label: t('label.year'),
      filterProps: {
        id: 'status-filter',
        multiple: true,
        options: yearsOptions || [],
        loading: isYearsOptionsLoading,
        value: search.year,
        placeholder: t('label.year'),
        onChange: value => handleChangeFilter('year', value)
      }
    },
    {
      label: t('label.date-range'),
      variant: 'date-range',
      size: 'wide',
      filterProps: {
        id: 'range',
        value: {
          from: search.dateAfter ? new Date(search.dateAfter) : undefined,
          to: search.dateBefore ? new Date(search.dateBefore) : undefined
        },
        onChange: value => {
          handleChangeFilter('dateAfter', value?.from)
          handleChangeFilter('dateBefore', value?.to)
        }
      }
    },
    {
      label: t('label.search'),
      variant: 'input',
      filterProps: {
        id: 'search',
        value: searchValue,
        type: 'search',
        dataTestId: 'search',
        placeholder: t('label.search'),
        onChange: value => handleChangeSearch(value)
      }
    }
  ]

  const handleClearAll = () => {
    navigate({
      search: {
        page: 1,
        pageSize: DEFAULT_PAGE_SIZE,
        status: search.status
      }
    })
  }

  const isSomeFilterSelected = checkIfFilterSelected(filters)

  const actions: (row: SchoolBreak) => TableAction<SchoolBreak>[] = row =>
    user?.isSuperAdmin
      ? [
          {
            icon: <EditIcon />,
            text: t('button.edit'),
            hidden: row.status === 'past',
            onClick: (item: SchoolBreak) => {
              navigate({
                to: '/school-settings/breaks/$breakId/edit',
                params: { breakId: item.id }
              })
            }
          }
        ].filter(item => !item.hidden)
      : []

  const download = useSchoolBreaksDownload({
    ...search,
    columnsKey: manageColumns.activeColumnKeys
  })

  return (
    <>
      <Filters
        filters={filters}
        onClearAll={handleClearAll}
        disabledClearAllButton={!isSomeFilterSelected}
        manageColumns={manageColumns}
        actionsRender={
          <>
            <ExportButton {...download} />
            {user?.isSuperAdmin ? (
              <Button
                asLink
                to="/school-settings/breaks/add"
                icon={<AddIcon />}
              >
                {t('button.add-new-school-break')}
              </Button>
            ) : null}
          </>
        }
      />

      <div className={styles.toggleWrapper}>
        <ToggleGroup
          value={search.status}
          onChange={handleToggle}
          options={toggleOptions}
        />
      </div>

      <Table
        isLoading={isSchoolBreaksLoading || isSchoolBreaksCountLoading}
        id="school-breaks-table"
        data={schoolBreaks?.list || []}
        columns={columns}
        count={schoolBreaks?.count}
        totalCount={schoolBreaksCount}
        actions={actions}
        {...pagination}
        caption={t('header.school-breaks')}
      />
    </>
  )
}
export default SchoolBreaksView
